import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, ViewEncapsulation } from '@angular/core';
import { ModalKeyboardDirective } from '../modal-keyboard/modal-keyboard.directive';

@Component({
  selector: 'lib-quick-view-modal-shell',
  standalone: true,
  imports: [CommonModule, ModalKeyboardDirective],
  encapsulation: ViewEncapsulation.None,
  template: `
    @if (isOpen) {
      <div class="quick-view-modal__backdrop" (click)="close.emit()">
        <section
          class="quick-view-modal"
          [style.--quick-view-width]="width"
          (click)="$event.stopPropagation()"
          [libModalKeyboard]="true"
          [libModalKeyboardCanCancel]="canClose"
          [libModalKeyboardCanSubmit]="canSubmit"
          [libModalKeyboardAutofocus]="autofocus"
          (libModalKeyboardCancel)="close.emit()"
          (libModalKeyboardSubmit)="submit.emit()"
          role="dialog"
          aria-modal="true"
          [attr.aria-label]="title"
        >
          <header class="quick-view-modal__head">
            <div class="quick-view-modal__title-wrap">
              <h3 class="quick-view-modal__title">{{ title }}</h3>
              @if (subtitleText) {
                <p class="quick-view-modal__subtitle">{{ subtitleText }}</p>
              }
            </div>

            @if (showCloseButton && canClose) {
              <button
                type="button"
                class="quick-view-modal__close"
                (click)="close.emit()"
                aria-label="Close quick view"
              >
                ×
              </button>
            }
          </header>

          @if (isLoading) {
            <div class="quick-view-modal__state">{{ loadingText }}</div>
          } @else if (errorText) {
            <div class="quick-view-modal__error">{{ errorText }}</div>
          } @else {
            <ng-content />
          }
        </section>
      </div>
    }
  `,
  styles: [`
    .quick-view-modal__backdrop {
      position: fixed;
      inset: 0;
      z-index: 1200;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 20px;
      background: rgba(15, 23, 42, 0.52);
      backdrop-filter: blur(6px);
    }

    .quick-view-modal {
      width: min(var(--quick-view-width, 760px), 100%);
      max-height: min(90vh, 960px);
      overflow: auto;
      border: 1px solid rgba(226, 232, 240, 0.94);
      border-radius: 16px;
      background: #ffffff;
      box-shadow: 0 28px 80px rgba(15, 23, 42, 0.24);
      padding: 20px;
      box-sizing: border-box;
      display: grid;
      gap: 16px;
    }

    .quick-view-modal__head {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 16px;
    }

    .quick-view-modal__title-wrap {
      min-width: 0;
    }

    .quick-view-modal__title {
      margin: 0;
      color: #111827;
      font-size: 22px;
      line-height: 1.18;
      font-weight: 800;
    }

    .quick-view-modal__subtitle,
    .quick-view-modal__state {
      margin: 6px 0 0;
      color: #64748b;
      font-size: 14px;
      line-height: 1.45;
    }

    .quick-view-modal__close {
      width: 42px;
      height: 42px;
      border-radius: 999px;
      border: 1px solid #cbd5e1;
      background: #ffffff;
      color: #475569;
      cursor: pointer;
      font-size: 26px;
      line-height: 1;
      flex: 0 0 auto;
    }

    .quick-view-modal__close:focus-visible,
    .quick-view-modal__close:focus,
    .quick-view-action:focus-visible,
    .quick-view-action:focus,
    .quick-view-form input:focus-visible,
    .quick-view-form input:focus,
    .quick-view-form textarea:focus-visible,
    .quick-view-form textarea:focus {
      outline: 2px solid #2563eb;
      outline-offset: 2px;
    }

    .quick-view-modal__error {
      border: 1px solid #fecaca;
      border-radius: 10px;
      background: #fef2f2;
      color: #b91c1c;
      padding: 10px 12px;
      font-size: 14px;
      line-height: 1.4;
    }

    .quick-view-grid,
    .quick-view-form {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 12px;
    }

    .quick-view-field,
    .quick-view-form label {
      display: grid;
      gap: 6px;
      min-width: 0;
      padding: 12px;
      border: 1px solid #e5e7eb;
      border-radius: 10px;
      background: #f8fafc;
      color: #334155;
      font-size: 12px;
      font-weight: 700;
      line-height: 1.2;
    }

    .quick-view-field--wide,
    .quick-view-form__wide {
      grid-column: 1 / -1;
    }

    .quick-view-field span,
    .quick-view-form label {
      text-transform: uppercase;
      letter-spacing: 0.04em;
      color: #64748b;
    }

    .quick-view-field strong,
    .quick-view-form input,
    .quick-view-form textarea {
      min-width: 0;
      color: #111827;
      font-size: 14px;
      font-weight: 600;
      line-height: 1.45;
      overflow-wrap: anywhere;
      text-transform: none;
      letter-spacing: 0;
    }

    .quick-view-form input,
    .quick-view-form textarea {
      border: 0;
      border-radius: 0;
      background: transparent;
      padding: 0;
      font: inherit;
    }

    .quick-view-form textarea {
      min-height: 72px;
      resize: vertical;
    }

    .quick-view-id-meta {
      margin-top: 2px;
      color: #64748b;
      font-size: 11px;
      line-height: 1.3;
      font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
      word-break: break-all;
      text-transform: none;
      letter-spacing: 0;
    }

    .quick-view-profile {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 12px;
      border: 1px solid #e5e7eb;
      border-radius: 10px;
      background: #f8fafc;
    }

    .quick-view-photo {
      width: 56px;
      height: 56px;
      border-radius: 10px;
      object-fit: cover;
      flex: 0 0 auto;
    }

    .quick-view-name {
      color: #111827;
      font-size: 17px;
      font-weight: 800;
      line-height: 1.25;
    }

    .quick-view-chips {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
    }

    .quick-view-chip {
      border: 1px solid #dbeafe;
      border-radius: 999px;
      background: #eff6ff;
      color: #1d4ed8;
      padding: 5px 9px;
      font-size: 12px;
      font-weight: 700;
      line-height: 1.2;
    }

    .quick-view-actions {
      grid-column: 1 / -1;
      display: flex;
      justify-content: flex-end;
      gap: 10px;
    }

    .quick-view-action {
      border: 1px solid #cbd5e1;
      border-radius: 8px;
      background: #ffffff;
      color: #0f172a;
      padding: 8px 12px;
      font-weight: 700;
      cursor: pointer;
    }

    @media (max-width: 720px) {
      .quick-view-modal__backdrop {
        padding: 8px;
        align-items: flex-end;
      }

      .quick-view-modal {
        width: 100%;
        max-height: min(92dvh, calc(100dvh - 12px));
        border-bottom-right-radius: 0;
        border-bottom-left-radius: 0;
        padding: 16px;
      }

      .quick-view-grid,
      .quick-view-form {
        grid-template-columns: 1fr;
      }
    }
  `],
})
export class QuickViewModalShellComponent {
  @Input() public isOpen = false;
  @Input() public title = '';
  @Input() public subtitle: string | null = '';
  @Input() public loadingText = 'Loading...';
  @Input() public errorText: string | null = null;
  @Input() public isLoading = false;
  @Input() public canClose = true;
  @Input() public showCloseButton = true;
  @Input() public canSubmit = false;
  @Input() public autofocus = true;
  @Input() public width = '760px';

  @Output() public readonly close = new EventEmitter<void>();
  @Output() public readonly submit = new EventEmitter<void>();

  protected get subtitleText(): string {
    return this.subtitle?.trim() ?? '';
  }
}
